// src/components/organisation/renderers/EventsSectionRenderer.jsx
import React from 'react';
import { Calendar, MapPin, Clock, Users, ExternalLink } from 'lucide-react';

export const EventsSectionRenderer = ({ 
  data, 
  bookData, 
  customStyling = {}, 
  onInteraction 
}) => {
  const {
    heading = 'Upcoming Events',
    subHeading = '',
    events = [],
    layout = 'grid',
    enabled = true
  } = data || {};

  if (!enabled || !events?.length) return null;

  const trackInteraction = (action, details = {}) => {
    if (onInteraction) {
      onInteraction({
        type: 'events_interaction',
        action,
        section: 'events',
        sectionId: data?.id,
        ...details
      });
    }
  };

  const handleEventClick = (event) => {
    trackInteraction('event_click', { eventTitle: event.title });
    if (event.registrationUrl) {
      window.open(event.registrationUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const time = new Date(`1970-01-01T${timeString}`);
    return time.toLocaleTimeString('en-ZA', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <section className="events-section py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Section header */}
        {(heading || subHeading) && (
          <div className="text-center mb-16">
            {heading && (
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                {heading}
              </h2>
            )}
            {subHeading && (
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {subHeading}
              </p>
            )}
          </div>
        )}

        {/* Events grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {events.map((event, index) => (
            <div 
              key={index}
              className="event-card bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 cursor-pointer group"
              onClick={() => handleEventClick(event)}
            >
              {/* Event image */}
              {event.image && (
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={event.image}
                    alt={event.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
              )}
              
              {/* Event content */}
              <div className="p-6">
                {/* Date badge */}
                {event.date && (
                  <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium mb-4">
                    <Calendar className="h-4 w-4" />
                    {formatDate(event.date)}
                  </div>
                )}

                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                  {event.title}
                </h3>

                {event.description && (
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {event.description}
                  </p>
                )}

                {/* Event details */}
                <div className="space-y-2 mb-6">
                  {event.time && (
                    <div className="flex items-center gap-2 text-gray-500 text-sm">
                      <Clock className="h-4 w-4" />
                      {formatTime(event.time)}
                    </div>
                  )}
                  {event.location && (
                    <div className="flex items-center gap-2 text-gray-500 text-sm">
                      <MapPin className="h-4 w-4" />
                      {event.location}
                    </div>
                  )}
                  {event.capacity && (
                    <div className="flex items-center gap-2 text-gray-500 text-sm">
                      <Users className="h-4 w-4" />
                      {event.attendees || 0} / {event.capacity} attendees
                    </div>
                  )}
                </div>

                {/* Registration button */}
                {event.registrationUrl && (
                  <div className="flex items-center justify-between">
                    <span className="text-blue-600 font-medium">Register now</span>
                    <ExternalLink className="h-4 w-4 text-blue-600" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};